import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

import {
  GET_CAMPAIGN,
  GET_CAMPAIGN_LOGS,
  GET_CAMPAIGN_TARGETS,
  GET_CAMPAIGN_TARGET_CONTACT_IDS,
  GET_MESSAGE_TEMPLATES,
} from '../../gql/queries/campaigns'
import {
  ADD_CAMPAIGN_TARGETS,
  CLEAR_CAMPAIGN_TARGETS,
  DELETE_CAMPAIGN,
  DELETE_CAMPAIGN_TARGET,
  LAUNCH_CAMPAIGN,
  PAUSE_CAMPAIGN,
  SEND_CAMPAIGN_TEST_MESSAGE,
  SYNC_CAMPAIGN_TARGETS,
  UPDATE_CAMPAIGN,
  UPDATE_MESSAGE_TEMPLATE,
} from '../../gql/mutations/campaigns'
import {
  CampaignChannel,
  ICampaign,
  ICampaignLogsRes,
  ICampaignRes,
  ICampaignTargetsRes,
  IMessageTemplate,
  IMessageTemplatesRes,
} from '../../types/campaigns'
import { CampaignDetail } from './components/CampaignDetail'
import { CampaignFormModal, CampaignFormValues } from './components/CampaignFormModal'
import { TemplateFormModal } from './components/TemplateFormModal'
import { AddTargetModal } from './components/AddTargetModal'
import { CampaignsNav } from './components/CampaignsNav'


export const CampaignDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [targetModalOpen, setTargetModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)

  const {
    data: campaignData,
    loading: loadingCampaign,
    error: campaignError,
    refetch: refetchCampaign,
  } = useQuery<ICampaignRes>(GET_CAMPAIGN, {
    skip: !id,
    variables: { id },
    fetchPolicy: 'cache-and-network',
    pollInterval: autoRefresh ? 30000 : 0,
  })

  const {
    data: targetsData,
    loading: loadingTargets,
    refetch: refetchTargets,
  } = useQuery<ICampaignTargetsRes>(GET_CAMPAIGN_TARGETS, {
    skip: !id,
    variables: { campaignId: id },
    fetchPolicy: 'no-cache',
  })

  const { data: allTargetIdsData, refetch: refetchAllTargetIds } = useQuery(GET_CAMPAIGN_TARGET_CONTACT_IDS, {
    skip: !id || !targetModalOpen,
    variables: { campaignId: id },
    fetchPolicy: 'no-cache',
  })
  const existingTargetContactIds: string[] = (allTargetIdsData?.campaignTargets?.items ?? []).map(
    (item: any) => item.contact.id
  )

  const {
    data: logsData,
    loading: loadingLogs,
    refetch: refetchLogs,
  } = useQuery<ICampaignLogsRes>(GET_CAMPAIGN_LOGS, {
    skip: !id,
    variables: { campaignId: id },
    fetchPolicy: 'no-cache',
    pollInterval: autoRefresh ? 30000 : 0,
  })

  const { data: templatesData, refetch: refetchTemplates } = useQuery<IMessageTemplatesRes>(
    GET_MESSAGE_TEMPLATES,
    { fetchPolicy: 'cache-and-network' }
  )

  const campaign: ICampaign | undefined = campaignData?.campaign

  const [updateCampaignMutation, { loading: updatingCampaign }] = useMutation(UPDATE_CAMPAIGN)
  const [stopCampaignMutation, { loading: stoppingCampaign }] = useMutation(UPDATE_CAMPAIGN)
  const [updateTemplateMutation, { loading: updatingTemplate }] = useMutation(UPDATE_MESSAGE_TEMPLATE)
  const [addTargetsMutation, { loading: addingTarget }] = useMutation(ADD_CAMPAIGN_TARGETS)
  const [syncTargetsMutation, { loading: syncingTargets }] = useMutation(SYNC_CAMPAIGN_TARGETS)
  const [launchCampaignMutation, { loading: launchingCampaign }] = useMutation(LAUNCH_CAMPAIGN)
  const [pauseCampaignMutation, { loading: pausingCampaign }] = useMutation(PAUSE_CAMPAIGN)
  const [sendTestMutation] = useMutation(SEND_CAMPAIGN_TEST_MESSAGE)
  const [deleteCampaignTargetMutation] = useMutation(DELETE_CAMPAIGN_TARGET)
  const [clearCampaignTargetsMutation, { loading: clearingTargets }] = useMutation(CLEAR_CAMPAIGN_TARGETS)
  const [deleteCampaignMutation] = useMutation(DELETE_CAMPAIGN)

  const handleSubmitCampaign = async (values: CampaignFormValues) => {
    if (!campaign) return
    try {
      await updateCampaignMutation({
        variables: {
          id: campaign.id,
          name: values.name,
          description: values.description,
          enabledChannels: values.enabledChannels,
          strategy: values.strategy,
          scheduleType: values.scheduleType,
          scheduledTime: values.scheduledTime,
          recurringRule: values.recurringRule || null,
          targetSegment: values.targetSegment,
          templateId: values.templateId,
          metadata: values.metadata,
        },
      })
      toast.success('Campaign updated')
      setCampaignModalOpen(false)
      await refetchCampaign()
    } catch {
      toast.error('There was a problem saving the campaign')
    }
  }

  const handleSubmitTemplate = async (payload: {
    name: string
    description?: string
    channel: CampaignChannel
    subject?: string
    body: string
    variables?: string[]
    metadata?: Record<string, any>
  }) => {
    if (!editingTemplate) return
    try {
      await updateTemplateMutation({ variables: { id: editingTemplate.id, ...payload } })
      toast.success('Template updated')
      setTemplateModalOpen(false)
      await refetchTemplates()
    } catch {
      toast.error('Something went wrong saving the template')
    }
  }

  const handleAddTarget = async (
    targets: { contactId: string; metadata?: Record<string, any>; channelOverrides?: Record<string, any> }[]
  ) => {
    if (!id) return
    try {
      await addTargetsMutation({
        variables: {
          campaignId: id,
          targets: targets.map((t) => ({
            contactId: t.contactId,
            metadata: t.metadata || null,
            channelOverrides: t.channelOverrides || null,
          })),
        },
      })
      toast.success(targets.length > 1 ? `${targets.length} targets added` : 'Target added')
      setTargetModalOpen(false)
      await Promise.all([refetchCampaign(), refetchTargets()])
    } catch {
      toast.error('Unable to add targets')
    }
  }

  const handleSyncTargets = async (selectedContactIds: string[]) => {
    if (!id) return
    try {
      const result = await syncTargetsMutation({
        variables: { campaignId: id, contactIds: selectedContactIds },
      })
      const added = result.data?.syncCampaignTargets?.addedCount ?? 0
      const removed = result.data?.syncCampaignTargets?.removedCount ?? 0
      const parts: string[] = []
      if (added > 0) parts.push(`${added} added`)
      if (removed > 0) parts.push(`${removed} removed`)
      toast.success(parts.length > 0 ? `Targets updated: ${parts.join(', ')}` : 'No changes')
      setTargetModalOpen(false)
      await Promise.all([refetchCampaign(), refetchTargets(), refetchAllTargetIds()])
    } catch {
      toast.error('Unable to save targets')
    }
  }

  const handleLaunchCampaign = async () => {
    if (!id) return
    try {
      await launchCampaignMutation({ variables: { id, runAsync: true } })
      toast.success('Campaign queued for delivery')
      await Promise.all([refetchCampaign(), refetchLogs()])
    } catch {
      toast.error('Unable to launch campaign')
    }
  }

  const handlePauseCampaign = async () => {
    if (!id) return
    try {
      await pauseCampaignMutation({ variables: { id } })
      toast.success('Campaign paused')
      await refetchCampaign()
    } catch {
      toast.error('Unable to pause the campaign')
    }
  }

  const handleStopCampaign = async () => {
    if (!id) return
    try {
      await stopCampaignMutation({ variables: { id, status: 'COMPLETED' } })
      toast.success('Recurring campaign stopped')
      await refetchCampaign()
    } catch {
      toast.error('Unable to stop campaign')
    }
  }

  const handleDeleteCampaign = async () => {
    if (!id) return
    try {
      await deleteCampaignMutation({ variables: { id } })
      toast.success('Campaign deleted')
      navigate('/campaigns')
    } catch {
      toast.error('Could not delete campaign')
    }
  }

  const handleRemoveTarget = async (targetId: string) => {
    try {
      await deleteCampaignTargetMutation({ variables: { id: targetId } })
      toast.success('Target removed')
      await Promise.all([refetchTargets(), refetchCampaign()])
    } catch {
      toast.error('Could not remove target')
    }
  }

  const handleClearTargets = async () => {
    if (!id) return
    try {
      const { data: result } = await clearCampaignTargetsMutation({ variables: { campaignId: id } })
      const deleted = result?.clearCampaignTargets?.deletedCount ?? 0
      toast.success(`${deleted} target${deleted !== 1 ? 's' : ''} removed`)
      await Promise.all([refetchTargets(), refetchCampaign()])
    } catch {
      toast.error('Could not clear targets')
    }
  }

  const handleSendTest = async (
    recipient: string,
    channels: CampaignChannel[],
    advancedOptions?: { fromEmail?: string; fromName?: string; replyTo?: string; includeLogo?: boolean }
  ) => {
    if (!campaign?.template?.id) {
      toast.error('Please select a template before sending tests')
      return
    }
    try {
      await sendTestMutation({
        variables: { templateId: campaign.template.id, recipient, channels, ...advancedOptions },
      })
      toast.success('Test message sent')
    } catch {
      toast.error('Unable to send test message')
      throw new Error('Send test failed')
    }
  }

  const templates = templatesData?.messageTemplates ?? []

  if (campaignError) {
    return <Alert variant='danger'>Unable to load campaign: {campaignError.message}</Alert>
  }

  return (
    <div className='campaign-detail-page'>
      <CampaignsNav />
      <div className='d-flex align-items-center gap-3 mb-6'>
        <button
          className='btn btn-sm btn-light'
          onClick={() => navigate('/campaigns')}
        >
          <i className='bi bi-arrow-left me-1'></i>
          Back
        </button>
        {loadingCampaign && <Spinner animation='border' size='sm' />}
      </div>

      <CampaignDetail
        campaign={campaign}
        targetsData={targetsData?.campaignTargets}
        logsData={logsData?.campaignLogs}
        loadingTargets={loadingTargets}
        loadingLogs={loadingLogs}
        onEditCampaign={() => setCampaignModalOpen(true)}
        onAddTarget={() => setTargetModalOpen(true)}
        onLaunchCampaign={handleLaunchCampaign}
        onPauseCampaign={handlePauseCampaign}
        launching={launchingCampaign}
        pausing={pausingCampaign}
        onSendTest={handleSendTest}
        onEditTemplate={(template) => {
          setEditingTemplate(template)
          setTemplateModalOpen(true)
        }}
        onRefreshTargets={() => refetchTargets()}
        onRefreshLogs={() => refetchLogs()}
        onRemoveTarget={handleRemoveTarget}
        onClearTargets={handleClearTargets}
        clearingTargets={clearingTargets}
        onStopCampaign={handleStopCampaign}
        stopping={stoppingCampaign}
        onDelete={handleDeleteCampaign}
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={setAutoRefresh}
      />

      <CampaignFormModal
        show={campaignModalOpen}
        onHide={() => setCampaignModalOpen(false)}
        onSubmit={handleSubmitCampaign}
        templates={templates}
        loading={updatingCampaign}
        campaign={campaign ?? null}
      />
      <TemplateFormModal
        show={templateModalOpen}
        onHide={() => setTemplateModalOpen(false)}
        onSubmit={handleSubmitTemplate}
        template={editingTemplate}
        loading={updatingTemplate}
      />
      <AddTargetModal
        show={targetModalOpen}
        onHide={() => setTargetModalOpen(false)}
        onSubmit={handleAddTarget}
        onSync={handleSyncTargets}
        loading={addingTarget || syncingTargets}
        campaignName={campaign?.name}
        campaignId={id}
        initialSelectedIds={existingTargetContactIds}
        onBulkAdded={() => {
          refetchCampaign()
          refetchTargets()
          refetchAllTargetIds()
        }}
      />
    </div>
  )
}
